import { ColorEnum } from '@/constants/color'
import {
    CustomerLookupAddressSection,
    CustomerLookupCustomer,
    RightPanelState,
} from '@/features/pos/components/CustomerProfile/types'
import { getCustomerLookupPhone } from '@/features/pos/components/CustomerProfile/utils'
import LocationIcon from '@/features/ui/icons/components/LocationIcon'
import PersonIcon from '@/features/ui/icons/components/PersonIcon'
import PhoneIcon from '@/features/ui/icons/components/PhoneIcon'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'

interface RightPanelProps {
    addressSections: CustomerLookupAddressSection[]
    compact?: boolean
    customerResults: CustomerLookupCustomer[]
    onAddressSelect: (addressId: string) => void
    onCustomerSelect: (customer: CustomerLookupCustomer) => void
    searchedPhone?: string
    searchedPhoneDialCode?: string
    state: RightPanelState
}

const RightPanel: FC<RightPanelProps> = ({
    addressSections,
    compact = false,
    customerResults,
    onAddressSelect,
    onCustomerSelect,
    searchedPhone,
    searchedPhoneDialCode,
    state,
}) => {
    const { t } = useTranslation()

    if (state.type === 'idle') {
        return (
            <View style={[styles.centeredState, compact && styles.centeredStateCompact]}>
                <Typography.Heading
                    style={[styles.placeholderHeading, compact && styles.placeholderHeadingCompact]}
                    variant={compact ? 'h3' : 'h2'}
                >
                    {t('pos.customerLookupModal.emptyState.title')}
                </Typography.Heading>
                <Typography.Heading
                    style={[
                        styles.placeholderHeading,
                        styles.placeholderSubtitle,
                        compact && styles.placeholderHeadingCompact,
                    ]}
                    variant={compact ? 'h3' : 'h2'}
                >
                    {t('pos.customerLookupModal.emptyState.subtitle')}
                </Typography.Heading>
            </View>
        )
    }

    if (state.type === 'customer-results') {
        return (
            <View style={styles.resultsContainer}>
                <Typography.Heading style={styles.sectionTitle} variant="h5">
                    {t('pos.customerLookupModal.results.customersTitle')}
                </Typography.Heading>
                <ScrollView
                    contentContainerStyle={styles.scrollAreaContent}
                    showsVerticalScrollIndicator={false}
                    style={styles.scrollArea}
                >
                    <View style={styles.resultsBox}>
                        <View style={styles.tableHeader}>
                            <Typography.Button size="small" style={styles.tableHeaderText}>
                                {t('pos.customerLookupModal.results.phone')}
                            </Typography.Button>
                            <Typography.Button size="small" style={styles.tableHeaderText}>
                                {t('pos.customerLookupModal.results.name')}
                            </Typography.Button>
                        </View>
                        <View style={styles.tableRows}>
                            {customerResults.length > 0 ? (
                                customerResults.map((customer) => (
                                    <Pressable
                                        key={customer.uuid}
                                        onPress={() => onCustomerSelect(customer)}
                                        style={styles.tableRow}
                                    >
                                        <View style={styles.tableCell}>
                                            <PhoneIcon
                                                fill={ColorEnum.BRICK_600}
                                                height={16}
                                                width={16}
                                            />
                                            <Typography.Description
                                                font="medium"
                                                numberOfLines={1}
                                                style={[
                                                    styles.tableRowText,
                                                    compact && styles.tableRowTextCompact,
                                                ]}
                                            >
                                                {getCustomerLookupPhone(
                                                    customer,
                                                    searchedPhone,
                                                    searchedPhoneDialCode
                                                )}
                                            </Typography.Description>
                                        </View>
                                        <View style={styles.tableCell}>
                                            <PersonIcon
                                                fill={ColorEnum.BRICK_600}
                                                height={16}
                                                width={16}
                                            />
                                            <Typography.Description
                                                font="medium"
                                                numberOfLines={1}
                                                style={[
                                                    styles.tableRowText,
                                                    compact && styles.tableRowTextCompact,
                                                ]}
                                            >
                                                {customer.name || customer.names[0] || ''}
                                            </Typography.Description>
                                        </View>
                                    </Pressable>
                                ))
                            ) : (
                                <View style={styles.centeredInnerState}>
                                    <Typography.Description
                                        font="medium"
                                        style={styles.emptyPanelText}
                                    >
                                        {t('pos.customerLookupModal.results.noCustomers')}
                                    </Typography.Description>
                                </View>
                            )}
                        </View>
                    </View>
                </ScrollView>
            </View>
        )
    }

    if (state.type === 'address-results') {
        return (
            <View style={styles.resultsContainer}>
                <Typography.Heading style={styles.sectionTitle} variant="h5">
                    {t('pos.customerLookupModal.results.addressesTitle')}
                </Typography.Heading>
                <ScrollView
                    contentContainerStyle={styles.scrollAreaContent}
                    showsVerticalScrollIndicator={false}
                    style={styles.scrollArea}
                >
                    <View style={styles.resultsBox}>
                        {addressSections.length > 0 ? (
                            <View style={styles.addressSections}>
                                {addressSections.map((section) => (
                                    <View key={section.id} style={styles.addressSection}>
                                        {section.title ? (
                                            <Typography.Description
                                                font="medium"
                                                style={styles.addressSectionTitle}
                                            >
                                                {section.title}
                                            </Typography.Description>
                                        ) : null}
                                        {section.options.map((option) => (
                                            <Pressable
                                                key={option.id}
                                                onPress={() => onAddressSelect(option.id)}
                                                style={[
                                                    styles.addressRow,
                                                    option.isSelected && styles.addressRowActive,
                                                ]}
                                            >
                                                <LocationIcon
                                                    fill={
                                                        option.isSelected
                                                            ? ColorEnum.WHITE
                                                            : ColorEnum.BRICK_500
                                                    }
                                                    height={18}
                                                    width={18}
                                                />
                                                <Typography.Description
                                                    font="medium"
                                                    style={[
                                                        styles.addressRowText,
                                                        option.isSelected &&
                                                            styles.addressRowTextActive,
                                                    ]}
                                                >
                                                    {option.address}
                                                </Typography.Description>
                                            </Pressable>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        ) : (
                            <View style={styles.centeredInnerState}>
                                <Typography.Description font="medium" style={styles.emptyPanelText}>
                                    {t('pos.customerLookupModal.results.noAddresses')}
                                </Typography.Description>
                            </View>
                        )}
                    </View>
                </ScrollView>
            </View>
        )
    }

    return null
}

const styles = StyleSheet.create({
    resultsContainer: {
        backgroundColor: ColorEnum.GRAY_100,
        flex: 1,
        minHeight: 0,
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
    },
    scrollAreaContent: {
        flexGrow: 1,
        paddingBottom: 32,
    },
    sectionTitle: {
        color: ColorEnum.GRAY_900,
        marginBottom: 8,
    },
    resultsBox: {
        backgroundColor: ColorEnum.VIOLET_100,
        borderColor: ColorEnum.GRAY_300,
        borderRadius: 12,
        borderWidth: 1,
        padding: 12,
    },
    centeredState: {
        alignItems: 'center',
        flex: 1,
        justifyContent: 'center',
        paddingHorizontal: 24,
    },
    centeredStateCompact: {
        alignSelf: 'stretch',
        flex: 0,
        minHeight: 160,
        paddingBottom: 16,
        paddingTop: 16,
        width: '100%',
    },
    centeredInnerState: {
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 168,
        padding: 24,
    },
    placeholderHeading: {
        color: ColorEnum.JET_100,
        textAlign: 'center',
    },
    placeholderHeadingCompact: {
        fontSize: 20,
        lineHeight: 28,
    },
    placeholderSubtitle: {
        marginTop: 8,
    },
    emptyPanelText: {
        color: ColorEnum.JET_100,
        textAlign: 'center',
    },
    tableHeader: {
        flexDirection: 'row',
        marginBottom: 8,
        paddingHorizontal: 12,
    },
    tableHeaderText: {
        color: ColorEnum.GRAY_900,
        flex: 1,
    },
    tableRows: {
        gap: 4,
    },
    tableRow: {
        alignItems: 'center',
        backgroundColor: ColorEnum.WHITE,
        borderRadius: 8,
        flexDirection: 'row',
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    tableCell: {
        alignItems: 'center',
        flex: 1,
        flexDirection: 'row',
        gap: 8,
        minWidth: 0,
    },
    tableRowText: {
        color: ColorEnum.GRAY_900,
        flex: 1,
        flexShrink: 1,
    },
    tableRowTextCompact: {
        fontSize: 12,
        lineHeight: 16,
    },
    addressSections: {
        gap: 16,
    },
    addressSection: {
        gap: 8,
    },
    addressSectionTitle: {
        color: ColorEnum.GRAY_600,
    },
    addressRow: {
        alignItems: 'center',
        backgroundColor: ColorEnum.WHITE,
        borderRadius: 8,
        flexDirection: 'row',
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    addressRowActive: {
        backgroundColor: ColorEnum.BRICK_600,
    },
    addressRowText: {
        color: ColorEnum.GRAY_900,
        flex: 1,
    },
    addressRowTextActive: {
        color: ColorEnum.WHITE,
    },
})

export default RightPanel
